import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  stuacademicyearlist,
  stuclasslist,
  studivisionlist,
} from "../../../services/master";
import { academylist, listtimetable } from "../../../services/admin";

// Read-only timetable report. Pick academy (super-admin), class and section
// (plus academic year + type, which the timetable API requires) and the saved
// timetable grid is rendered with the same look as the Timetable screen.
const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];

const TimetableReport = () => {
  const isSuperAdmin = localStorage.getItem("Role") === "1";

  const [aclist, setACList] = useState([]);
  const [acaid, setAcaID] = useState("");
  const [ylist, setYList] = useState([]);
  const [ayear, setAYear] = useState("");
  const [clist, setCList] = useState([]);
  const [sclass, setSClass] = useState("");
  const [divlist, setDivList] = useState([]);
  const [studiv, setStuDiv] = useState("");
  const [ttype, setTType] = useState("Students");
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    stuclasslist().then((res) => {
      if (res.status === true && res.data.length > 0) {
        setCList(
          res.data.map((item) => ({ value: item.id, label: item.class_name }))
        );
      }
    });

    studivisionlist().then((res) => {
      if (res.status === true && res.data.length > 0) {
        setDivList(
          res.data.map((item) => ({ value: item.id, label: item.name }))
        );
      }
    });

    academylist().then((res) => {
      if (res.status === true && res.data.details.length > 0) {
        const arr = res.data.details.map((item) => ({
          value: item.id,
          label: item.name + " - " + item.code,
        }));
        setACList(arr);

        // Default to the academy chosen at login (matches other report screens).
        let defaultAca = "";
        if (localStorage.getItem("AAcademy") != null) {
          defaultAca =
            arr.find((a) => a.value == localStorage.getItem("AAcademy")) || "";
          if (defaultAca) {
            setAcaID(defaultAca);
            if (localStorage.getItem("AYear")) {
              setAYear(localStorage.getItem("AYear"));
            }
          }
        }
        listyear(defaultAca);
      } else {
        listyear("");
      }
    });
  }, []);

  function listyear(aid) {
    const temp = new FormData();
    if (isSuperAdmin && aid && aid.value) {
      temp.append("academy_id", aid.value);
    }
    stuacademicyearlist(temp).then((res) => {
      if (res.status === true) {
        setYList(res.data || []);
      }
    });
  }

  function handleAcademy(e) {
    setAcaID(e || "");
    setAYear("");
    setPeriods([]);
    listyear(e || "");
  }

  function searchTimetable() {
    if (ayear === "") return;
    if (sclass === "") return;
    if (studiv === "") return;

    setPeriods([]);
    const temp = new FormData();
    temp.append("class", sclass.value);
    temp.append("year", ayear);
    temp.append("section", studiv.value);
    temp.append("type", ttype);
    if (isSuperAdmin && acaid && acaid.value) {
      temp.append("academy", acaid.value);
    }

    listtimetable(temp).then((res) => {
      if (res.status === true) {
        if (res.data.length > 0) {
          setPeriods(
            res.data.map((item) => ({
              id: item.id,
              time: item.timeperiod,
              name: item.t_lecture,
              academy: item.academy_name,
              schedule: [
                item.monday,
                item.tuesday,
                item.wednesday,
                item.thursday,
                item.friday,
                item.saturday,
              ],
            }))
          );
        }
      }
    });
  }

  const exportToCSV = () => {
    const q = (v) => `"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`;

    // Selected-filter context, shown at the top of the report.
    const yearLabel = (ylist.find((y) => y.id == ayear) || {}).aca_year || "";
    const meta = [
      ["Academic Year", yearLabel],
      ["Class", sclass ? sclass.label : ""],
      ["Section", studiv ? studiv.label : ""],
      ["Type", ttype],
    ].map((row) => row.map(q).join(","));

    const header = ["Lecture", "Time", ...DAYS.map((d) => d.label)];
    const lines = periods.map((p) =>
      [p.name, p.time, ...p.schedule].map(q).join(",")
    );
    const csv = [...meta, "", header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Timetable Report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="main-content-title tx-24 mg-b-5">Timetable Report</h2>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Reports</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Timetable Report
          </li>
        </ol>
      </div>
      <div className="content-body">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h2 className="main-content-title tx-24 mg-b-5">
                Timetable Report
              </h2>
            </div>
            <div className="card-body">
              <div className="row">
                {isSuperAdmin && (
                  <div className="col-xl-3 col-sm-6">
                    <label className="form-label">Select Academy</label>
                    <Select
                      className="default-select"
                      placeholder="Select Academy"
                      value={acaid}
                      options={aclist}
                      onChange={handleAcademy}
                      isSearchable={true}
                      isClearable
                    />
                  </div>
                )}

                <div className="col-xl-3 col-sm-6">
                  <label className="form-label">Academic Year</label>
                  <select
                    className="default-select form-control wide"
                    value={ayear}
                    onChange={(e) => setAYear(e.target.value)}
                  >
                    <option value="" disabled>
                      Select Academic Year
                    </option>
                    {ylist.map((item) => (
                      <option value={item.id} key={item.id}>
                        {item.aca_year}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-xl-3 col-sm-6">
                  <label className="form-label">Select Class</label>
                  <Select
                    className="default-select"
                    placeholder="Select Class"
                    value={sclass}
                    options={clist}
                    onChange={(e) => setSClass(e || "")}
                    isSearchable={true}
                    isClearable
                  />
                </div>

                <div className="col-xl-3 col-sm-6">
                  <label className="form-label">Select Section</label>
                  <Select
                    className="default-select"
                    placeholder="Select Section"
                    value={studiv}
                    options={divlist}
                    onChange={(e) => setStuDiv(e || "")}
                    isSearchable={true}
                    isClearable
                  />
                </div>

                <div className="col-xl-3 col-sm-6 mt-2">
                  <label className="form-label">Type</label>
                  <select
                    className="default-select form-control wide"
                    value={ttype}
                    onChange={(e) => setTType(e.target.value)}
                  >
                    <option value="Students">Students</option>
                    <option value="Teachers">Teachers</option>
                  </select>
                </div>

                <div className="col-xl-6 col-sm-6">
                  <div className="res-right mt-5">
                    <button
                      className="btn btn-info me-2"
                      onClick={searchTimetable}
                    >
                      View Report
                    </button>
                    <button
                      className="btn btn-success me-2"
                      onClick={exportToCSV}
                      disabled={periods.length === 0}
                    >
                      Excel Download
                    </button>
                  </div>
                </div>
              </div>

              <div className="timetable">
                <table>
                  <thead>
                    <tr>
                      <th>Lecture / Day</th>
                      {DAYS.map((d) => (
                        <th key={d.key}>{d.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {periods.length === 0 ? (
                      <tr>
                        <td colSpan={DAYS.length + 1}>No records found</td>
                      </tr>
                    ) : (
                      periods.map((period, index) => (
                        <tr
                          key={period.id || index}
                          className={index % 2 === 0 ? "even" : "odd"}
                        >
                          <td>
                            <div className="period-name">{period.name}</div>
                            <div className="period-time">{period.time}</div>
                          </td>
                          {period.schedule.map((cell, i) => (
                            <td key={i}>{cell}</td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimetableReport;
